import { Component, ElementRef, ViewChild, AfterViewInit, Renderer2, HostListener, Input, Output, EventEmitter, OnChanges, SimpleChanges, input } from '@angular/core';

@Component({
  selector: 'app-stop-point-slider', // Changed selector
  templateUrl: './stop-point-slider.component.html', // Changed template file name
  styleUrls: ['./stop-point-slider.component.scss'] // Changed style file name
})
export class StopPointSliderComponent implements AfterViewInit, OnChanges { // Changed class name

  @Input() stopPoints: number[] = []; // Input for stop points
  @Input() selectedValue: number = this.stopPoints[0]; // Input for initial value
  @Output() valueChange = new EventEmitter<number>(); // Output for value changes
  @Input() isSliderDisabled = false;

  @ViewChild('sliderTrack') sliderTrack!: ElementRef;
  @ViewChild('sliderThumb') sliderThumb!: ElementRef;

  normalizedPositions: number[] = [];
  trackWidth: number = 0;
  isDragging: boolean = false;

  constructor(private renderer: Renderer2) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Recalculate if stopPoints or selectedValue inputs change
    if (changes['stopPoints'] || (changes['selectedValue'] && changes['selectedValue'].isFirstChange())) {
      this.calculatePositions();
      // Ensure the initial selected value is one of the stop points
      if (this.stopPoints.includes(this.selectedValue)) {
        // Set initial position after view is initialized
        if (this.sliderThumb) {
          this.setThumbPosition(this.selectedValue);
        }
      } else if (this.stopPoints.length > 0) {
        // Default to the first stop point if the provided value isn't valid
        this.selectedValue = this.stopPoints[0];
        this.valueChange.emit(this.selectedValue);
      }
    }

    if (changes['selectedValue'] && !changes['selectedValue'].isFirstChange()) {
      this.setThumbPosition(this.selectedValue);
    }
  }

  ngAfterViewInit() {
    this.trackWidth = this.sliderTrack.nativeElement.offsetWidth;
    this.calculatePositions(); // Ensure positions are calculated after view init

    this.setThumbPosition(this.selectedValue);

    this.renderer.listen(this.sliderTrack.nativeElement, 'click', (event: MouseEvent) => {
      this.handleTrackClick(event);
    });
  }

  calculatePositions() {
    if (this.stopPoints.length === 0) {
      this.normalizedPositions = [];
      return;
    }
    const min = this.stopPoints[0];
    const max = this.stopPoints[this.stopPoints.length - 1];

    // Check if min and max are the same to avoid division by zero
    if (min === max) {
      this.normalizedPositions = this.stopPoints.map(() => 0.5); // Center if all points are the same
    } else {
      this.normalizedPositions = this.stopPoints.map(p => (p - min) / (max - min));
    }

    // Set initial position if it's the first render or if stop points changed
    if (this.sliderThumb) {
      this.setThumbPosition(this.selectedValue);
    }
  }

  setThumbPosition(value: number) {
    const index = this.stopPoints.indexOf(value);
    if (index === -1) return;

    const percent = this.normalizedPositions[index];
    const thumbOffset = 20;

    this.renderer.setStyle(
      this.sliderThumb.nativeElement,
      'left',
      `calc(${percent * 100}% - ${thumbOffset * percent}px)`
    );
  }


  handleTrackClick(event: MouseEvent) {
    const clickX = event.clientX - this.sliderTrack.nativeElement.getBoundingClientRect().left;
    const clickRatio = clickX / this.trackWidth;

    this.updateValueFromPosition(clickRatio);
  }

  startDrag(event: MouseEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;

    const trackLeft = this.sliderTrack.nativeElement.getBoundingClientRect().left;
    const posX = event.clientX - trackLeft;
    const ratio = Math.min(Math.max(posX / this.trackWidth, 0), 1);

    this.updateValueFromPosition(ratio);
  }

  @HostListener('document:mouseup')
  endDrag() {
    this.isDragging = false;
  }

  updateValueFromPosition(positionRatio: number) {
    // Find closest stop point by comparing position ratio
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.normalizedPositions.forEach((pos, i) => {
      const distance = Math.abs(pos - positionRatio);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    const newValue = this.stopPoints[closestIndex];

    if (newValue !== this.selectedValue) {
      this.selectedValue = newValue;
      this.setThumbPosition(this.selectedValue);
      this.valueChange.emit(this.selectedValue); // Emit the new value
    }
  }
}